// CartPage.js (упрощенный вариант)
import React, { useState, useEffect, useCallback } from "react";
import { useCart } from '../context/cartContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/firestore';
import {
  collection,
  addDoc,
  getDoc,
  setDoc,
  doc,
  Timestamp,
  getDocs,
  deleteDoc,
} from 'firebase/firestore';
import { useUser } from '../context/userContext';
import FavoriteButton from '../Components/FavoriteButton';
import CheckoutModal from '../Components/CheckoutModal'; // Импортируем новый компонент

// Объявляем глобальную переменную Telegram для ESLint
/* global Telegram */

// Функция для правильного склонения слова "товар"
const formatItemCount = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return `${count} товаров`;
  }
  
  if (lastDigit === 1) {
    return `${count} товар`;
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${count} товара`;
  }
  
  return `${count} товаров`;
};

const CartPage = () => {
  const { cartItems, removeFromCart, clearCart, updateQuantity, getTotalItems } = useCart();
  const navigate = useNavigate();
  const { id: userId, fullName, telegramUser } = useUser();

  const handleTelegramBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    if (window.Telegram?.WebApp?.BackButton) {
      const backButton = window.Telegram.WebApp.BackButton;

      backButton.show();
      backButton.onClick(handleTelegramBack);

      return () => {
        backButton.offClick(handleTelegramBack);
        backButton.hide();
      };
    }
  }, [handleTelegramBack]);

  
  const [selectAll, setSelectAll] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const selectedTotal = cartItems
    .filter(item => selectedItems.includes(item.id))
    .reduce((sum, item) => sum + item.price * item.quantity, 0);
  const selectedCount = selectedItems.length;

  // Состояния для модального окна
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [fullNameInput, setFullNameInput] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [street, setStreet] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [deliveryType, setDeliveryType] = useState(''); // 'mkad' или 'outside_mkad'
  const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash' или 'bank_transfer'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  // Рассчитываем стоимость доставки
  const deliveryCost = deliveryType === 'mkad' ? 800 : deliveryType === 'outside_mkad' ? 1300 : 0;
  const totalWithDelivery = selectedTotal + deliveryCost;

  // Загружаем данные пользователя при монтировании компонента
  useEffect(() => {
    const loadUserData = async () => {
      const uid = userId || telegramUser?.id?.toString();
      if (!uid) return;

      try {
        const userDocRef = doc(db, 'telegramUsers', uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Устанавливаем значения из профиля пользователя
          setFullNameInput(userData.fullName || fullName || telegramUser?.first_name || '');
          setEmail(userData.email || '');
          setPhone(userData.phone || '');
          setBirthDate(userData.birthDate || '');
          
          // Загружаем сохраненный адрес, если есть
          if (userData.savedAddress) {
            setStreet(userData.savedAddress.street || '');
          }
        } else {
          // Если документ не существует, используем данные из контекста
          setFullNameInput(fullName || telegramUser?.first_name || '');
        }
      } catch (error) {
        console.error('Ошибка загрузки данных пользователя:', error);
        setFullNameInput(fullName || telegramUser?.first_name || '');
      }
    };

    loadUserData();
  }, [userId, fullName, telegramUser]);

  // Сохраняем данные пользователя в профиль
  const saveUserProfile = async () => {
    const uid = userId || telegramUser?.id?.toString();
    if (!uid) return;

    try {
      const userData = {
        fullName: fullNameInput.trim(),
        email: email.trim(),
        phone: phone.trim(),
        birthDate: birthDate,
        updatedAt: Timestamp.now(),
        // Сохраняем также адрес для будущих заказов
        savedAddress: {
          street: street.trim(),
          deliveryInstructions: deliveryInstructions.trim(),
          updatedAt: Timestamp.now()
        }
      };

      const userDocRef = doc(db, 'telegramUsers', uid);
      await setDoc(userDocRef, userData, { merge: true });
      console.log('Профиль пользователя сохранен:', userData);
    } catch (error) {
      console.error('Ошибка сохранения профиля пользователя:', error);
    }
  };

  // Инициализация выбранных товаров при загрузке
  useEffect(() => {
    const itemIds = cartItems.map(item => item.id);
    setSelectedItems(itemIds);
    setSelectAll(itemIds.length > 0);
  }, [cartItems]);

  // Обновляем состояние "Выбрать все" при изменении выбранных товаров
  useEffect(() => {
    if (cartItems.length > 0) {
      setSelectAll(selectedItems.length === cartItems.length);
    }
  }, [selectedItems, cartItems]);

  // Обработчик выбора всех товаров
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(cartItems.map(item => item.id));
    }
    setSelectAll(!selectAll);
  };

  // Обработчик выбора отдельного товара
  const handleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  // Функция для расчета скидки
  const calculateDiscount = (item) => {
    if (item.originalPrice) {
      const discount = Math.round((1 - item.price / item.originalPrice) * 100);
      return {
        currentPrice: item.price,
        originalPrice: item.originalPrice,
        discount: discount
      };
    }
    
    const originalPrice = Math.round(item.price * 1.16);
    const discount = Math.round((1 - item.price / originalPrice) * 100);
    return {
      currentPrice: item.price,
      originalPrice: originalPrice,
      discount: discount
    };
  };

  // ИСПРАВЛЕННАЯ ФУНКЦИЯ "ПОДЕЛИТЬСЯ" ДЛЯ TELEGRAM
  const handleShare = async () => {
    const selectedCartItems = cartItems.filter(item => selectedItems.includes(item.id));
    
    if (selectedCartItems.length === 0) {
      alert('Выберите товары для общего доступа');
      return;
    }
    
    // Формируем текст для шаринга
    const shareText = `🛒 Моя корзина покупок:\n\n` +
      selectedCartItems.map(item => 
        `• ${item.title} - ${item.quantity} шт. × ${item.price.toLocaleString()}₽ = ${(item.price * item.quantity).toLocaleString()}₽`
      ).join('\n') +
      `\n\n💰 Итого: ${selectedTotal.toLocaleString()}₽`;
    
    // Проверяем, находимся ли мы в Telegram WebApp
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      try {
        console.log('Используем Telegram Share API');
        
        // Используем нативный метод Telegram для шаринга
        window.Telegram.WebApp.shareMessage(
          shareText,
          { url: window.location.href } // Опционально: ссылка на корзину
        );
        
        // Telegram сам откроет интерфейс выбора чата для отправки
      } catch (error) {
        console.error('Ошибка при использовании Telegram Share API:', error);
        
        // Fallback: показываем всплывающее окно с информацией для копирования
        window.Telegram.WebApp.showAlert(`Скопируйте текст для обмена:\n\n${shareText}`);
      }
    } else {
      // Fallback для браузера (разработка/тестирование)
      console.warn('Telegram WebApp не обнаружен, используется браузерный fallback');
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Моя корзина покупок',
            text: shareText,
            url: window.location.href,
          });
        } catch (err) {
          console.error('Браузерный share ошибка:', err);
          // Если не сработал share, копируем в буфер
          navigator.clipboard.writeText(shareText);
          alert('Текст корзины скопирован в буфер обмена!');
        }
      } else {
        // Старый браузер - просто копируем
        navigator.clipboard.writeText(shareText);
        alert('Текст корзины скопирован в буфер обмена!');
      }
    }
  };

  const handleCheckout = async () => {
    if (selectedCount === 0) {
      alert('Выберите товары для оформления заказа');
      return;
    }

    const selectedCartItems = cartItems.filter(item => selectedItems.includes(item.id));
    
    // Проверка обязательных полей
    if (!fullNameInput.trim()) {
      alert('Пожалуйста, укажите ваше ФИО');
      return;
    }
    
    if (!phone || !/^\+?\d{7,15}$/.test(phone)) {
      alert('Пожалуйста, укажите корректный номер телефона');
      return;
    }
    
    // Проверка новых полей
    if (!deliveryDate) {
      alert('Пожалуйста, выберите дату доставки');
      return;
    }
    
    if (!deliveryTime) {
      alert('Пожалуйста, выберите время доставки');
      return;
    }
    
    if (!street.trim()) {
      alert('Пожалуйста, укажите адрес доставки');
      return;
    }
    
    if (!deliveryType) {
      alert('Пожалуйста, выберите тип доставки');
      return;
    }

    setIsSubmitting(true);

    try {
      const uid = userId || telegramUser?.id?.toString();
      if (!uid) {
        alert('Ошибка: пользователь не идентифицирован.');
        setIsSubmitting(false);
        return;
      }

      // Сохраняем профиль пользователя (включая birthDate)
      await saveUserProfile();

      const deliveryAddress = { 
        street: street.trim(),
        deliveryInstructions: deliveryInstructions.trim(),
        deliveryDate,
        deliveryTime,
        deliveryType,
        deliveryCost
      };
      
      const orderData = {
        userId: uid,
        fullName: fullNameInput.trim(),
        email: email.trim(),
        phone: phone.trim(),
        birthDate: birthDate,
        username: telegramUser?.username || '',
        createdAt: Timestamp.now(),
        status: 'pending',
        paymentMethod,
        deliveryAddress,
        items: selectedCartItems.map(({ id, title, price, quantity, image }) => ({
          id, 
          title, 
          price, 
          quantity, 
          image,
        })),
        subtotal: selectedTotal,
        deliveryCost,
        total: totalWithDelivery,
      };

      // Сохраняем заказ в основной коллекции
      const orderRef = await addDoc(collection(db, 'orders'), orderData);
      
      // Сохраняем заказ в профиле пользователя
      await addDoc(collection(db, `telegramUsers/${uid}/orders`), {
        ...orderData,
        orderId: orderRef.id,
      });

      // Отправка уведомления админам
      try {
        const sanitizeText = (text) => text ? String(text).replace(/[*_`[\]()~>#+=|{}.!]/g, '') : '—';
        const deliveryInfo = `
Дата: ${deliveryDate}
Время: ${deliveryTime}
Адрес: ${sanitizeText(street)}
Тип доставки: ${deliveryType === 'mkad' ? 'Внутри МКАД (800₽)' : 'За МКАД до 10км (1300₽)'}
Инструкции: ${sanitizeText(deliveryInstructions) || 'не указаны'}
        `;
        
        const message = `
Новый заказ (#${orderRef.id.slice(-6)})
Пользователь: ${sanitizeText(fullNameInput)} (${sanitizeText(telegramUser?.username || 'без username')})
Телефон: ${sanitizeText(phone)}
Email: ${sanitizeText(email || 'не указан')}
День рождения: ${sanitizeText(birthDate || 'не указан')}
---
Информация о доставке:
${deliveryInfo}
---
Оплата: ${paymentMethod === 'cash' ? 'Наличными курьеру' : 'Переводом на карту'}
Сумма: ${sanitizeText(selectedTotal)}₽
Доставка: ${sanitizeText(deliveryCost)}₽
Итого: ${sanitizeText(totalWithDelivery)}₽
Товаров: ${sanitizeText(selectedCount)}
        `;

        await fetch("/api/notify-admins", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            order: { 
              ...orderData, 
              orderId: orderRef.id, 
              message 
            } 
          }),
        });
      } catch (notifyErr) {
        console.error("Ошибка уведомления админов:", notifyErr);
      }

      // Удаляем оформленные товары из корзины Firestore
      if (uid) {
        const cartRef = collection(db, `telegramUsers/${uid}/cartItems`);
        const cartSnapshot = await getDocs(cartRef);
        
        // Удаляем только оформленные товары
        const deletePromises = [];
        cartSnapshot.docs.forEach(docSnap => {
          const itemData = docSnap.data();
          if (selectedItems.includes(itemData.id)) {
            deletePromises.push(deleteDoc(docSnap.ref));
          }
        });
        
        if (deletePromises.length > 0) {
          await Promise.all(deletePromises);
        }
      }

      // Обновляем локальное состояние корзины
      selectedItems.forEach(itemId => {
        removeFromCart(itemId);
      });

      // Если оплата переводом, показываем инструкцию
      if (paymentMethod === 'bank_transfer') {
        setShowBankDetails(true);
      } else {
        setShowSuccessOverlay(true);
        setTimeout(() => {
          setShowSuccessOverlay(false);
          setIsModalOpen(false);
          setSelectedItems([]);
          navigate('/');
        }, 3000);
      }

    } catch (error) {
      console.error('Error while placing order:', error);
      alert('Произошла ошибка. Пожалуйста, попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Функция для закрытия окна с реквизитами и перехода к успеху
  const handleCloseBankDetails = () => {
    setShowBankDetails(false);
    setShowSuccessOverlay(true);
    setTimeout(() => {
      setShowSuccessOverlay(false);
      setIsModalOpen(false);
      setSelectedItems([]);
      navigate('/');
    }, 3000);
  };

  return (
    <div className="bg-[#EBF0F3] min-h-screen pb-24">
      {/* Заголовок и счетчик товаров */}
      <div className="bg-white p-4 shadow-sm sticky top-0 z-20">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-bold text-gray-900">Корзина</h2>
          <span className="text-gray-500 font-medium">{formatItemCount(cartItems.length)}</span>
        </div>

        {/* Выбрать все и Поделиться */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleSelectAll}
            className="flex items-center gap-2 text-gray-700 active:opacity-80 transition-opacity"
            disabled={cartItems.length === 0}
          >
            <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${selectAll ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
              {selectAll && (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-sm font-medium">Выбрать все</span>
          </button>
          
          <button
            onClick={handleShare}
            className="flex items-center gap-2 text-gray-700 active:opacity-80 transition-opacity"
            disabled={selectedCount === 0}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span className="text-sm font-medium">Поделиться</span>
          </button>
        </div>
      </div>

      {/* Список товаров */}
      <div className="p-4 space-y-3">
        {cartItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-medium text-gray-800 mb-3">В корзине пока пусто</h3>
            <p className="text-gray-500 mb-8 max-w-sm">
              Добавите что-нибудь с главной страницы или избранного
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 px-8 rounded-xl transition-all shadow-md hover:shadow-lg active:scale-95"
            >
              На главную
            </button>
          </div>
        ) : (
          <>
            {cartItems.map((item) => {
              const discountInfo = calculateDiscount(item);
              const isSelected = selectedItems.includes(item.id);
              const itemTotal = item.price * item.quantity;
              
              return (
                <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  {/* Чекбокс и основная информация */}
                  <div className="flex items-start gap-3 mb-3">
                    <button
                      onClick={() => handleSelectItem(item.id)}
                      className="flex-shrink-0 mt-1"
                    >
                      <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                    
                    <div className="flex-1">
                      {/* Цена со скидкой */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-bold text-gray-900">{itemTotal.toLocaleString()} ₽</span>
                        <span className="text-gray-400 line-through text-sm">
                          {Math.round(discountInfo.originalPrice * item.quantity).toLocaleString()} ₽
                        </span>
                        <span className="bg-red-100 text-red-600 text-xs font-semibold px-2 py-1 rounded">
                          -{discountInfo.discount}%
                        </span>
                      </div>
                      
                      {/* Название товара */}
                      <h3 className="font-medium text-gray-900 mb-3 line-clamp-2">{item.title}</h3>
                      
                      {/* Количество и цена за штуку */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-sm text-gray-600 gap-2">
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="font-medium">шт. {item.quantity}</span>
                            <span className="ml-2">цена за шт {item.price.toLocaleString()} ₽</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-4">
                          <FavoriteButton product={item} size="small" />
                          
                          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1">
                            <button
                              onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-200 active:bg-gray-300 transition-colors"
                            >
                              <span className="text-gray-700 text-lg">−</span>
                            </button>
                            <span className="min-w-[30px] text-center font-medium">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-200 active:bg-gray-300 transition-colors"
                            >
                              <span className="text-gray-700 text-lg">+</span>
                            </button>
                          </div>
                          
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      {/* Единица измерения */}
                      <div className="mt-3 text-sm text-gray-500 pt-2 border-t border-gray-100">
                        Единица измерения: {item.unit || 'шт'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Итоговая панель */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 fixed bottom-4 left-4 right-4 z-10">
              <div className="flex justify-between items-center mb-4">
                <div className="text-gray-700">
                  <div className="text-sm text-gray-500">Выбрано</div>
                  <div className="font-medium">{formatItemCount(selectedCount)}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Сумма</div>
                  <div className="text-lg font-bold text-gray-900">{selectedTotal.toLocaleString()} ₽</div>
                </div>
              </div>
              
              <button
                onClick={() => setIsModalOpen(true)}
                disabled={selectedCount === 0}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white py-3 rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
              >
                К оформлению
              </button>
            </div>
          </>
        )}
      </div>
      

<CheckoutModal
  isOpen={isModalOpen}
  onClose={() => setIsModalOpen(false)}
  selectedCount={selectedCount}
  selectedTotal={selectedTotal}
  totalWithDelivery={totalWithDelivery}
  deliveryType={deliveryType}
  paymentMethod={paymentMethod}
  deliveryCost={deliveryCost}
  isSubmitting={isSubmitting}
  showBankDetails={showBankDetails}
  showSuccessOverlay={showSuccessOverlay}
  // Формы
  fullNameInput={fullNameInput}
  setFullNameInput={setFullNameInput}
  email={email}
  setEmail={setEmail}
  phone={phone}
  setPhone={setPhone}
  birthDate={birthDate}
  setBirthDate={setBirthDate}
  street={street}
  setStreet={setStreet}
  deliveryDate={deliveryDate}
  setDeliveryDate={setDeliveryDate}
  deliveryTime={deliveryTime}
  setDeliveryTime={setDeliveryTime}
  deliveryInstructions={deliveryInstructions}
  setDeliveryInstructions={setDeliveryInstructions}
  setDeliveryType={setDeliveryType}
  setPaymentMethod={setPaymentMethod}
  // Обработчики
  handleCheckout={handleCheckout}
  handleCloseBankDetails={handleCloseBankDetails}
  setShowBankDetails={setShowBankDetails} // Добавляем этот проп
  navigate={navigate}
/>
    </div>
  );
};

export default CartPage;